import { OrganizationLogo } from '@features/organizations'
import { Title } from '@st/react-util/title'
import { PublicOrganization, PublicUser } from '@st/sdk'
import { Button, Dialog, DialogButtons, FullPageCard, Modal, Spinner, TextInput } from '@st/theme'
import { isValidEmail } from '@st/util/email'
import { ReactNode, useState } from 'react'
import { match, P } from '@st/pattern'
import { ClientLoginFlowMessage, ClientLoginFlowState } from './client-login-flow-module'

export function ClientLoginPage({
  organization,
  title,
  subtitle,
  possibleUsers,
  email,
  children,
  state,
  send
}: {
  organization: PublicOrganization
  title: ReactNode
  subtitle: ReactNode
  possibleUsers: PublicUser[]
  email?: {
    value: string
    onChange: (email: string) => void
  }
  children: ReactNode
  state: ClientLoginFlowState
  send: (message: ClientLoginFlowMessage) => void
}) {
  return match(state)
    .with({ status: P.union('idle', 'loading', 'finishing') }, () => <ClientLoginLoadingCard />)
    .with(
      P.union(
        { status: 'loggedIn' },
        { status: 'lookupPending' },
        { status: 'navigatingToFolder' },
        { status: 'lookupChooseFolder' },
        { status: 'lookupFailed' }
      ),
      () => <>{children}</>
    )
    .otherwise(() => (
      <ClientLoginView
        state={state}
        organization={organization}
        title={title}
        subtitle={subtitle}
        possibleUsers={possibleUsers}
        email={email}
        send={send}
      />
    ))
}

export function ClientLoginLoadingCard() {
  return (
    <FullPageCard>
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <Spinner className="size-12 fill-blue-500 text-blue-100" />
        <span>Loading...</span>
      </div>
    </FullPageCard>
  )
}

export function ClientLoginView({
  state,
  organization,
  title,
  subtitle,
  possibleUsers,
  email,
  send
}: {
  state: ClientLoginFlowState
  organization: PublicOrganization
  title: ReactNode
  subtitle: ReactNode
  possibleUsers: PublicUser[]
  email?: {
    value: string
    onChange: (email: string) => void
  }
  send: (message: ClientLoginFlowMessage) => void
}) {
  const [showUserSelector, setShowUserSelector] = useState(false)
  const logo = organization.logo
  const emailIsValid = email ? isValidEmail(email.value) : true

  return (
    <FullPageCard
      footer={
        <p className="mt-10 text-center text-sm text-gray-500">
          Powered by StanfordTax, Inc.
          <br /> 222 Broadway, New York, 10032
        </p>
      }
    >
      <Title>{organization.name}</Title>
      <div className="flex flex-col gap-1">
        {logo && <OrganizationLogo className="mx-auto" src={logo} size={80} />}

        <h3 className="text-center text-xl font-bold leading-9 tracking-tight text-gray-900">
          {organization.name ?? ' '}
        </h3>
      </div>

      <h1 className="mt-8 text-center text-3xl">{title}</h1>
      <h2 className="mb-8 text-center text-2xl">{subtitle}</h2>

      {email && (
        <div className="mb-6 flex flex-col items-center gap-4">
          <p className="my-4 text-center text-base">Please enter your email address</p>
          <TextInput
            type="email"
            placeholder="Enter your email"
            className="w-3/4"
            value={email.value}
            onChange={email.onChange}
          />
        </div>
      )}

      {match(state)
        .with(
          P.union(
            { status: 'login', loginMethod: 'emailVerification' },
            { status: 'sendingVerificationEmail' },
            { status: 'clickEmailVerificationLink' }
          ),
          () => {
            return (
              <>
                <Button
                  variant="primary"
                  className="m-auto"
                  disabled={state.status == 'loading' || possibleUsers.length == 0 || !emailIsValid}
                  leadingIcon={
                    state.status == 'sendingVerificationEmail' ? (
                      <Spinner className="size-4 fill-white text-blue-100" />
                    ) : null
                  }
                  onClick={() => {
                    if (possibleUsers.length > 1) {
                      // Multiple emails available; ask which one to verify.
                      setShowUserSelector(true)
                    } else {
                      send({
                        type: 'clickSendVerificationEmail',
                        recipientUserId: possibleUsers[0].id
                      })
                    }
                  }}
                >
                  {match(state)
                    .with(
                      { status: 'login', loginMethod: 'emailVerification' },
                      () => 'Send sign-in link'
                    )
                    .with({ status: 'sendingVerificationEmail' }, () => 'Sending sign-in link')
                    .with({ status: 'clickEmailVerificationLink' }, () => 'Resend link')
                    .otherwise(() => null)}
                </Button>
                {state.status !== 'clickEmailVerificationLink' && (
                  <p className="mt-2 text-center text-sm text-gray-600">
                    We'll email you a secure link to access your organizer.
                  </p>
                )}
              </>
            )
          }
        )
        .otherwise(() => null)}

      {match(state)
        .with({ status: 'login', authError: P.nonNullable }, (s) => (
          <p className="mt-2 text-center text-sm text-red-600" role="alert" aria-live="assertive">
            {s.authError.message}
          </p>
        ))
        .with(
          { status: 'login', loginMethod: 'emailVerification' },
          () =>
            possibleUsers.length === 0 &&
            email == null && (
              <p className="mt-2 text-center text-sm text-gray-600">
                No email addresses have been granted access to this questionnaire. Please contact us
                to grant you access.
              </p>
            )
        )
        .otherwise(() => null)}

      {showUserSelector && (
        <Modal isOpen={showUserSelector}>
          <UserSelector
            users={possibleUsers}
            onClose={() => setShowUserSelector(false)}
            onSelect={(user) => {
              setShowUserSelector(false)
              send({
                type: 'clickSendVerificationEmail',
                recipientUserId: user.id
              })
            }}
          />
        </Modal>
      )}

      {match(state)
        .with({ status: 'clickEmailVerificationLink' }, (state) => (
          <div className="mt-2 flex flex-col gap-0.5 text-center text-sm text-gray-600">
            <div>
              We sent a sign-in link to{' '}
              {possibleUsers.find((u) => u.id === state.selectedUserId)?.email}.
            </div>
            <div>It may take a minute. Check spam if you don't see it.</div>
          </div>
        ))
        .otherwise(() => null)}
    </FullPageCard>
  )
}

function UserSelector({
  users,
  onClose,
  onSelect
}: {
  users: PublicUser[]
  onClose: () => void
  onSelect: (user: PublicUser) => void
}) {
  return (
    <Dialog
      title="Select your email"
      subtitle="Please select the email you would like to send the sign-in link to."
      buttons={
        <DialogButtons>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-col items-stretch gap-2">
        {users.map((user) => (
          <Button variant="default" size="l" key={user.id} onClick={() => onSelect(user)}>
            {user.email}
          </Button>
        ))}
      </div>
    </Dialog>
  )
}

export function QuestionnaireNotFound({ folderId }: { folderId: string }) {
  return (
    <FullPageCard>
      <div className="flex flex-col items-center gap-1">
        <div className="text-center text-lg">
          <p>Questionnaire with id {folderId} was not found.</p>
        </div>
      </div>
    </FullPageCard>
  )
}
